import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { switchMap, finalize, catchError, tap } from 'rxjs/operators';

export interface TransactionsReportQuery {
  fechaInicio: string; // Formato: YYYY-MM-DD
  fechaFin?: string; // Formato: YYYY-MM-DD (opcional)
}

export interface ApiErrorResponse {
  statusCode: number;
  timestamp: string;
  path: string;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class TransactionsReportGeneralService {
  private apiUrl = environment.apiNest;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  // M�todo para obtener los headers con el token
  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session || !session.accessToken || !session.accessToken.jwtToken) {
      throw new Error('No se pudo obtener el token de sesi�n');
    }

    const token = session.accessToken.jwtToken.trim();
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      switch (error.status) {
        case 401:
          errorMessage =
            'Error de autenticaci�n. Por favor, inicie sesi�n nuevamente.';
          break;
        case 403:
          errorMessage = 'No tiene permisos para realizar esta acci�n.';
          break;
        case 400:
          errorMessage = 'Los par�metros proporcionados no son v�lidos.';
          break;
        case 404:
          errorMessage = 'El servicio no est� disponible en este momento.';
          break;
        case 500:
          errorMessage =
            'Error interno del servidor. Por favor, intente m�s tarde.';
          break;
        default:
          if (error.error?.message) {
            errorMessage = error.error.message;
          }
          break;
      }
    }

    return throwError(() => ({ message: errorMessage, originalError: error }));
  }

  private validateDateFormat(date: string): boolean {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    return dateRegex.test(date);
  }

  private validateQuery(query: TransactionsReportQuery): string | null {
    // Validar fechaInicio
    if (!query.fechaInicio) {
      return 'La fecha de inicio es requerida';
    }

    if (!this.validateDateFormat(query.fechaInicio)) {
      return 'La fecha de inicio debe tener el formato YYYY-MM-DD';
    }

    // Validar fechaFin si existe
    if (query.fechaFin) {
      if (!this.validateDateFormat(query.fechaFin)) {
        return 'La fecha de fin debe tener el formato YYYY-MM-DD';
      }

      // Validar que fechaInicio sea menor o igual a fechaFin
      const inicio = new Date(query.fechaInicio);
      const fin = new Date(query.fechaFin);

      if (inicio > fin) {
        return 'La fecha de inicio debe ser menor o igual a la fecha de fin';
      }
    }

    return null;
  }

  /**
   * Descarga el reporte de transacciones en formato CSV
   * @param query Par�metros de consulta (fechaInicio y fechaFin opcional)
   * @returns Observable con el blob del archivo CSV
   */
  downloadTransactionsReport(query: TransactionsReportQuery): Observable<Blob> {
    // Validar query antes de proceder
    const validationError = this.validateQuery(query);
    if (validationError) {
      return throwError(() => ({ message: validationError }));
    }

    // Construir par�metros de consulta
    let params = new HttpParams().set('fechaInicio', query.fechaInicio);

    if (query.fechaFin) {
      params = params.set('fechaFin', query.fechaFin);
    }

    // Activar el loading
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get(`${this.apiUrl}transactions-report`, {
          headers,
          params,
          responseType: 'blob', // Importante para recibir archivos
          observe: 'response',
        });
      }),
      tap((response) => {
        // Extraer el nombre del archivo del header Content-Disposition si existe
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
        }
      }),
      switchMap((response) => {
        if (response.body) {
          return [response.body];
        }
        throw new Error('No se recibi� contenido en la respuesta');
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        // Desactivar el loading cuando finalice
        this.loadingSubject.next(false);
      }),
    );
  }

  /**
   * Descarga el reporte y lo guarda autom�ticamente en el navegador
   * @param query Par�metros de consulta (fechaInicio y fechaFin opcional)
   * @param customFilename Nombre personalizado para el archivo (opcional)
   */
  downloadAndSaveReport(
    query: TransactionsReportQuery,
    customFilename?: string,
  ): Observable<void> {
    return this.downloadTransactionsReport(query).pipe(
      tap((blob) => {
        // Generar nombre de archivo
        const fechaFin = query.fechaFin || query.fechaInicio;
        const filename =
          customFilename ||
          `transacciones_${query.fechaInicio}_${fechaFin}.csv`;

        // Crear URL del blob
        const url = window.URL.createObjectURL(blob);

        // Crear elemento <a> temporal para descargar
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();

        // Limpiar URL del blob
        window.URL.revokeObjectURL(url);
      }),
      switchMap(() => [undefined]),
    );
  }
}
